import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { Group } from '../lib/types';
import { dateTime } from '../lib/labels';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Table,
  errorMessage,
  fieldError,
} from '../components/ui';

export function GroupsPage() {
  const { tenant, can } = useSession();
  const groups = useResource(() => api.get<Group[]>('/v1/groups', { tenant: true }), [tenant?.id]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(
        '/v1/groups',
        { name, ...(description.trim() ? { description } : {}) },
        { tenant: true },
      );
      setName('');
      setDescription('');
      await groups.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Grupos"
        description="Agrupe dispositivos por equipe ou local para aplicar políticas em conjunto."
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <Card title="Grupos da empresa">
          {groups.error != null && <Alert tone="error">{errorMessage(groups.error)}</Alert>}
          <Table
            head={['Nome', 'Descrição', 'Criado em']}
            empty={
              !groups.loading && groups.data?.length === 0 && <EmptyState>Nenhum grupo.</EmptyState>
            }
          >
            {groups.data?.map((g) => (
              <tr key={g.id}>
                <td className="px-4 py-2 font-medium text-slate-900">{g.name}</td>
                <td className="px-4 py-2 text-slate-600">{g.description ?? '—'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                  {dateTime.format(new Date(g.createdAt))}
                </td>
              </tr>
            ))}
          </Table>
        </Card>
        {can('groups:manage') && (
          <Card title="Novo grupo">
            <form onSubmit={submit} className="space-y-4" noValidate>
              {error != null && <Alert tone="error">{errorMessage(error)}</Alert>}
              <Input
                label="Nome"
                value={name}
                error={fieldError(error, 'name')}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                label="Descrição (opcional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Button type="submit" busy={busy} disabled={!name.trim()}>
                Criar grupo
              </Button>
            </form>
          </Card>
        )}
      </div>
    </>
  );
}
